import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config.js';
import { jwtDecode } from 'jwt-decode';
import api from '../../api/api.js';

import {
  Bell, DollarSign, Eye, HardHat, Heart, MessageSquare, Search, Settings, 
  Star, TrendingUp, Users, Wrench, Briefcase, Plus, MapPin, Clock, 
  CheckCircle, Folder, User, ShieldCheck, Lock, AlertTriangle, Hourglass,  // Added Icons
} from "lucide-react";

// --- RESTORED UI IMPORTS ---
import { Avatar, AvatarFallback, AvatarImage } from "../../_newprofiles/components/ui/Avatar.jsx";
import { Badge } from "../../_newprofiles/components/ui/Badge.jsx";
import { Button } from "../../_newprofiles/components/ui/Button.jsx"; 
import { Card, CardContent, CardHeader, CardTitle } from "../../_newprofiles/components/ui/Card.jsx";

import styles from "./_BusinessDashboard.module.css";
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS.jsx";
import SearchFormComponent from "./SearchFormComponent.jsx";
import imageLogo from "../../assets/images/LogoBuildStart03.png";
import ImportGoogleRatingModal from '../../components/Google/ImportGoogleRatingModal.jsx';
import CreateProjectModal from '../../components/Projects/CreateProjectModal'; 
import FindContractorsModal from '../../components/Projects/Modals/FindContractorsModal';

import { getOrCreateGeneralProject } from '../../services/GeneralProjectService';
import { getOrCreateGeneralMaintenanceProjectId } from '../../services/GeneralProjectService';

import showToast from '../../components/ShowToast';
import FeatureGuard from '../../components/Common/FeatureGuard.js';

const BusinessDashboardPureCSS = () => {
  const navigate = useNavigate();
  
  // --- USER STATE ---
  const [username, setUsername] = useState('');
  const [businessUserId, setBusinessUserId] = useState(null);
  const [loggedInBusinessId, setLoggedInBusinessId] = useState(null);
  
  // --- DASHBOARD STATE ---
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'work'
  
  // Data State
  const [jobLeads, setJobLeads] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [myPostedJobs, setMyPostedJobs] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(null);
  
  // Subscription Check State
  const [subStatus, setSubStatus] = useState({ isVerified: false, plan: 'Free', status: 'Inactive' });

  const [savedBusinessesData, setSavedBusinessesData] = useState([]);
  
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [isLoadingWork, setIsLoadingWork] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);

  // Modals
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isFindContractorsModalOpen, setIsFindContractorsModalOpen] = useState(false);

  // Check verification
    const [docCount, setDocCount] = useState(0);
    const [expiryStatus, setExpiryStatus] = useState({ 
        hasExpired: false, // RED STATE (Badge Lost)
        isExpiringSoon: false, // YELLOW STATE (Warning)
        expiredDocName: '' 
    });

  // --- INITIALIZATION ---
  useEffect(() => {
      const token = localStorage.getItem('token');
      if (token) {
          try {
              const decoded = jwtDecode(token);
              setUsername(decoded.UserName || 'User');
              setBusinessUserId(decoded.nameid);
              
              const storedBizId = localStorage.getItem('loggedInBusinessId');
              if (storedBizId) setLoggedInBusinessId(storedBizId);
              
              if (localStorage.getItem('shouldOnboard') === 'true') {
                  navigate('/business-onboarding-new');
              }
          } catch (error) {
              navigate('/login');
          }
      } else {
          navigate('/login');
      }
  }, [navigate]);

  // --- FETCH DATA ---
  useEffect(() => {
    if (loggedInBusinessId) {
      fetchDashboardData();
    }
  }, [loggedInBusinessId, businessUserId]);

  const fetchDashboardData = async () => {
      setIsLoadingProfile(true);
      try {
          // 1. Fetch Profile
          const profileRes = await api.get(`/api/BusinessProfile/getBusinessProfile?businessId=${loggedInBusinessId}&logView=false&ommitProfilePic=true`);
          setBusinessProfile(profileRes.data);

          // 2. Fetch Subscription Status (Distinct from Profile)
          // We need to know if they are Paid, regardless of verification
          try {
             const subRes = await api.get(`/api/Subscription/hasValidSubscription?bizid=${loggedInBusinessId}`);
             //console.log("subRes = ", subRes);
             setSubStatus(subRes.data); 
          } catch (e) {
             console.log("Sub fetch failed", e);
          }

          // 3. Check for Uploaded Documents
        // CHECK DOCUMENTS & EXPIRY
        try {
            const docsRes = await api.get(`/api/BusinessOnboarding/get-uploaded-documents?businessId=${loggedInBusinessId}`);
            const docs = docsRes.data || [];
            setDocCount(docs.length);

            console.log("docs = ", docs);

            // Calculate Date Logic
            const today = new Date();
            const thirtyDaysFromNow = new Date();
            thirtyDaysFromNow.setDate(today.getDate() + 30);

            let expired = false;
            let expiringSoon = false;
            let docName = '';

            docs.forEach(doc => {
                if (doc.documentExpiry) {
                    const expiry = new Date(doc.documentExpiry);
                    
                    // Priority 1: Already Expired
                    if (expiry < today) {
                        expired = true;
                        docName = doc.documentCategory;
                    }
                    // Priority 2: Expiring Soon (only if not already found an expired one)
                    else if (expiry < thirtyDaysFromNow && !expired) {
                        expiringSoon = true;
                        docName = doc.documentCategory;
                    }
                }
            });

            setExpiryStatus({ hasExpired: expired, isExpiringSoon: expiringSoon, expiredDocName: docName });

        } catch (e) { console.log("Docs check failed", e); }




      } catch (e) { console.error("Profile fetch error", e); }
      finally { setIsLoadingProfile(false); }

      setIsLoadingLeads(true);
      try {
          const leadsRes = await axios.get(`${API_BASE_URL}/api/Jobs/getMyJobsForBusiness?businessId=${loggedInBusinessId}`);
          setJobLeads(leadsRes.data || []);
      } catch (e) { console.error("Leads error", e); }
      finally { setIsLoadingLeads(false); }

      setIsLoadingWork(true);
      try {
          const projRes = await api.get(`/api/Projects/getmyprojects?userId=${loggedInBusinessId}&userType=BusinessUser`);
          const allProjects = projRes.data || [];
          const bucketProject = allProjects.find(p => {
             const name = p.name || p.Name;
             return name && name.toLowerCase() === "general maintenance";
          });
          const bucketId = bucketProject ? (bucketProject.projectId || bucketProject.ProjectId) : -1;

          // Filter out bucket project
          let filteredProjects = allProjects.filter(p => {
              const pId = p.projectId || p.ProjectId;
              return pId !== bucketId;
          });

          // Sort by ProjectId descending (Newest first)
          filteredProjects.sort((a, b) => {
             const idA = a.projectId || a.ProjectId || 0;
             const idB = b.projectId || b.ProjectId || 0;
             return idB - idA;
          });

          setMyProjects(filteredProjects);

          const jobsRes = await axios.get(`${API_BASE_URL}/api/Jobs/getmyactivejobsForBusiness?businessUserId=${businessUserId}`);
          const allJobs = jobsRes.data || [];
          const soloProjId = await getOrCreateGeneralMaintenanceProjectId(businessUserId, "Business");
          const soloJobs = allJobs.filter(j => {
              const jProjId = j.projectId || j.ProjectId;
              return jProjId === soloProjId || !jProjId || jProjId === -1;
          });

          // Sort by CreatedAt descending (Newest first)
          soloJobs.sort((a, b) => {
             const dateA = new Date(a.created_at || a.CreatedAt || 0);
             const dateB = new Date(b.created_at || b.CreatedAt || 0);
             return dateB - dateA;
          });

          setMyPostedJobs(soloJobs);

      } catch (e) { console.error("Work error", e); }
      finally { setIsLoadingWork(false); }

      if (businessUserId) {
          setIsLoadingSaved(true);
          try {
              const savedRes = await axios.get(
                  `${API_BASE_URL}/api/BusinessProfile/getSavedBusinesses?userType=Business&userId=${businessUserId}`
              );
              setSavedBusinessesData(savedRes.data || []);
          } catch (e) { console.error("Saved businesses error", e); }
          finally { setIsLoadingSaved(false); }
      }
  };

  // --- HANDLERS ---
  const handleEditProfile = () => navigate(`/business-onboarding-new`);
  const handleCreateProject = () => setIsCreateProjectModalOpen(true);
  
  const handlePostSubContractJob = () => {
      setIsFindContractorsModalOpen(true);
  };

  const handlePostJob = async () => {
      setIsFindContractorsModalOpen(false);
      try {
          const projectId = await getOrCreateGeneralMaintenanceProjectId(businessUserId, "Business");
          navigate('/job-detail-new', { 
              state: { mode: 'broadcast', projectId: projectId, taskName: '' } 
          });
      } catch (err) {
          showToast("error", "Could not initialize job.");
      }
  };

  const handleBrowsePros = async () => {
      setIsFindContractorsModalOpen(false);
      try {
          const projectId = await getOrCreateGeneralMaintenanceProjectId(businessUserId, "Business");
          navigate('/job-detail-new', { 
              state: { mode: 'invite', projectId: projectId, taskName: '' } 
          });
      } catch (err) {
          showToast("error", "Could not initialize job.");
      }
  };

  const handleImportSuccess = () => fetchDashboardData();
  const getFirstName = (name) => name?.split(' ')[0] || 'User';
  const isUrgent = (deadline) => deadline && deadline.toLowerCase().includes('urgently');

  // --- BADGE STATUS COMPONENT ---
  const renderBadgeStatus = () => {
      const isVerifiedInDB = businessProfile?.businesses?.isDocApproved; 

        //console.log("businessProfile?.businesses?.isDocApproved = ", businessProfile?.businesses?.isDocApproved);
        //console.log("businessProfile?.businesses?", businessProfile?.businesses);

        // isVerified means has paid subscription
      const isPaid = subStatus.isVerified; 
      const hasDocs = docCount > 0;
      
      // NEW: Date Checks
      const { hasExpired, isExpiringSoon, expiredDocName } = expiryStatus;





      // -----------------------------------------------------------
      // SCENARIO 1: DOCUMENT EXPIRED (CRITICAL - BADGE REVOKED)
      // -----------------------------------------------------------
      if (hasExpired) {
          return (
              <div className={styles.sidebarCard} style={{border:'2px solid #EF4444'}}>
                  <div className={styles.sidebarHeader} style={{background:'#FEF2F2', color:'#B91C1C'}}>
                      <AlertTriangle size={18} /> Verified Pro Badge Unavailable
                  </div>
                  <div style={{padding:'1rem'}}>
                      <p style={{fontSize:'0.85rem', color:'#B91C1C', marginBottom:'1rem', fontWeight:600}}>
                          Action Required: Your {expiredDocName} has expired.
                      </p>
                      <p style={{fontSize:'0.8rem', color:'#64748B', marginBottom:'1rem'}}>
                          Your <strong>Verified Pro Badge</strong> is hidden until you upload the renewed document.
                      </p>
                      <button 
                        className={styles.btnPrimary} 
                        style={{width:'100%', background:'#EF4444', border:'none', padding: '5px', borderRadius: '5px'}}
                        onClick={() => navigate(`/business-onboarding-new`)}
                      >
                          Update Document
                      </button>
                  </div>
              </div>
          );
      }

      // -----------------------------------------------------------
      // SCENARIO 2: EXPIRING SOON (WARNING - BADGE ACTIVE)
      // -----------------------------------------------------------
      if (isVerifiedInDB && isPaid && isExpiringSoon) {
          return (
              <div className={`${styles.sidebarCard} ${styles.sidebarCardGreenBorder}`}>
                  {/* Keep the Green Header because they are still Verified */}
                  <div className={styles.sidebarHeader} style={{color:'#166534'}}>
                      <ShieldCheck size={18} fill="#166534" color="white"/> Verified Pro Active
                  </div>
                  <div style={{padding:'1rem'}}>
                      {/* Insert Yellow Warning Box INSIDE the Active Card */}
                      <div style={{background:'#FFFBEB', border:'1px solid #F59E0B', borderRadius:'6px', padding:'10px', marginBottom:'10px'}}>
                          <p style={{fontSize:'0.8rem', color:'#B45309', margin:0, fontWeight:600, display:'flex', alignItems:'center', gap:'6px'}}>
                              <Clock size={14}/> Renewal Due Soon
                          </p>
                          <p style={{fontSize:'0.8rem', color:'#92400E', margin:'4px 0 8px 0'}}>
                              Your {expiredDocName} documentation expires in less than 30 days.
                          </p>
                          <button 
                              onClick={() => navigate(`/business-onboarding-new`)}
                              style={{fontSize:'0.75rem', padding:'4px 8px', background:'#F59E0B', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}
                          >
                              Update Now
                          </button>
                      </div>
                      <p style={{fontSize:'0.85rem', color:'#166534', marginBottom:'0', textAlign:'center'}}>
                          Your badge is currently live.
                      </p>
                  </div>
              </div>
          );
      }

      // -----------------------------------------------------------
      // SCENARIO 3: HAPPY PATH (Fully Verified & Paid & Current)
      // -----------------------------------------------------------
      if (isVerifiedInDB && isPaid) {
          return (
              <div className={`${styles.sidebarCard} ${styles.sidebarCardGreenBorder}`}>
                  <div className={styles.sidebarHeader} style={{color:'#166534'}}>
                      <ShieldCheck size={18} fill="#166534" color="white"/> Verified Pro Active
                  </div>
                  <div style={{padding:'1rem', textAlign:'center'}}>
                      <p style={{fontSize:'0.85rem', color:'#166534', marginBottom:'0'}}>
                          Your <strong>Verified Badge</strong> is live. You are appearing in top search results.
                      </p>
                  </div>
              </div>
          );
      }

      // -----------------------------------------------------------
      // SCENARIO 4: BADGE LOCKED (Verified BUT Unpaid)
      // -----------------------------------------------------------
      if (isVerifiedInDB && !isPaid) {
          return (
              <div className={styles.sidebarCard} style={{border:'2px solid #D97706'}}>
                  <div className={styles.sidebarHeader} style={{background:'#FFF7ED', color:'#D97706'}}>
                      <Lock size={18}/> Badge Locked
                  </div>
                  <div style={{padding:'1rem'}}>
                      <p style={{fontSize:'0.85rem', color:'#9A3412', marginBottom:'1rem'}}>
                          Your documents are approved! However, your <strong>Verified Badge</strong> is inactive because you don't have an active subscription.
                      </p>
                      <button 
                        className={styles.btnPrimary} 
                        style={{width:'100%', background:'#D97706', border:'none', padding: '5px', borderRadius: '5px'}}
                        onClick={() => navigate('/subscription/subscribenew')}
                      >
                          Subscribe to Unlock
                      </button>
                  </div>
              </div>
          );
      }

      // -----------------------------------------------------------
      // SCENARIO 5: PENDING/NO DOCS (Default)
      // -----------------------------------------------------------
      if (hasDocs) {
           return (
              <div className={styles.sidebarCard} style={{border:'1px solid #3B82F6'}}>
                  <div className={styles.sidebarHeader} style={{background:'#EFF6FF', color:'#1E40AF'}}>
                      <Clock size={18} /> Verification in Progress
                  </div>
                  <div style={{padding:'1rem'}}>
                      <p style={{fontSize:'0.85rem', color:'#334155', marginBottom:'1rem'}}>
                          We are reviewing your documents.
                      </p>
                      {!isPaid && (
                          <div style={{background:'#FFF7ED', padding:'10px', borderRadius:'6px', border:'1px solid #FED7AA'}}>
                              <p style={{fontSize:'0.8rem', color:'#9A3412', margin:0, fontWeight:600}}>
                                  💡 Tip: Activate subscription now so your badge goes live instantly upon approval.
                              </p>
                              <button onClick={() => navigate('/subscription/subscribenew')} style={{marginTop:'8px', fontSize:'0.8rem', color:'#D97706', background:'none', border:'none', textDecoration:'underline', cursor:'pointer'}}>View Plans</button>
                          </div>
                      )}
                  </div>
              </div>
          );
      }

      // No Docs uploaded yet
      return (
          <div className={styles.sidebarCard}>
              <div className={styles.sidebarHeader}>
                  <AlertTriangle size={18} color="#D97706"/> Accreditation Pending
              </div>
              <div style={{padding:'1rem'}}>
                  <p style={{fontSize:'0.85rem', color:'#64748B', marginBottom:'1rem'}}>
                      Upload your license and insurance to unlock the <strong>Verified Pro Badge</strong>.
                  </p>
                  <button className={styles.btnSecondary} style={{width:'100%'}} onClick={() => navigate(`/business-onboarding-new`)}>Start Verification</button>
              </div>
          </div>
      );
  };

  return (
    <div className={styles.dashboardContainer}>
      {/* HEADER */}
      <header className={styles.header}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <div className={styles.logoContainer}>
                <img src={imageLogo} alt="Build Start" style={{ height: '35px', marginRight: '10px' }} />
                <span className={styles.logoText}>BUILD START</span>
              </div>
              <div className={styles.professionalBadge}>Professional</div>
            </div>
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'home'}  />
          </div>
        </div>
      </header>

      <div className={styles.container}>
        
        {/* WELCOME */}
        <div style={{marginBottom:'2rem', marginTop:'1rem'}}>
          <h1 style={{fontSize:'1.8rem', fontWeight:'bold', marginBottom:'0.5rem'}}>Welcome back, {getFirstName(username)}! 👷‍♂️</h1>
          <p style={{color:'#64748B'}}>Here is what's happening with your business today.</p>
        </div>

        {/* --- 1. SLIM STATS STRIP --- */}
       <div className={styles.statsGrid}>
          <Card className={`${styles.statCard} ${styles.statCardBlue}`}>
            <CardContent className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div><p className={styles.statLabelBlue}>New Leads</p><p className={styles.statValue}>{businessProfile?.statistics?.jobLeadsReceived ?? '0'}</p></div>
                <Search className={styles.statIconBlue} />
              </div>
            </CardContent>
          </Card>
          <Card className={`${styles.statCard} ${styles.statCardAmber}`}>
            <CardContent className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div><p className={styles.statLabelAmber}>Jobs Quoted</p><p className={styles.statValue}>{businessProfile?.statistics?.quotesSent ?? '0'}</p></div>
                <MessageSquare className={styles.statIconAmber} />
              </div>
            </CardContent>
          </Card>
          <Card className={`${styles.statCard} ${styles.statCardGreen}`}>
            <CardContent className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div><p className={styles.statLabelGreen}>Jobs Won</p><p className={styles.statValue}>{businessProfile?.statistics?.jobsCompleted ?? '0'}</p></div>
                <DollarSign className={styles.statIconGreen} />
              </div>
            </CardContent>
          </Card>
          <Card className={`${styles.statCard} ${styles.statCardPurple}`}>
            <CardContent className={styles.statCardContent}>
              <div className={styles.statCardInfo}>
                <div><p className={styles.statLabelPurple}>Profile Views</p><p className={styles.statValue}>{businessProfile?.statistics?.profileViews ?? '0'}</p></div>
                <Eye className={styles.statIconPurple} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* --- 2. MAIN LAYOUT (Split View) --- */}
        <div className={styles.mainLayoutGrid}>
          
          {/* LEFT COLUMN: WORK STREAMS */}
          <div className={styles.leftCol}>
            <div className={styles.tabContainer}>
                <div className={styles.tabHeader}>
                    <button className={`${styles.tabButton} ${activeTab === 'leads' ? styles.activeTab : ''}`} onClick={() => setActiveTab('leads')}>
                        <Search size={16}/> Incoming Leads 
                        {!isLoadingLeads && jobLeads.length > 0 && <span style={{marginLeft:'6px', opacity:0.7, fontSize:'0.9em'}}>({jobLeads.length})</span>}
                    </button>
                    <button className={`${styles.tabButton} ${activeTab === 'work' ? styles.activeTab : ''}`} onClick={() => setActiveTab('work')}>
                        <Briefcase size={16}/> My Sub-Contracted Jobs
                        {!isLoadingWork && (myProjects.length + myPostedJobs.length) > 0 && <span style={{marginLeft:'6px', opacity:0.7, fontSize:'0.9em'}}>({myProjects.length + myPostedJobs.length})</span>}
                    </button>
                </div>

                <div className={styles.tabContent}>
                    {activeTab === 'leads' && (
                        <>
                            {isLoadingLeads && <p>Loading leads...</p>}
                            {!isLoadingLeads && jobLeads.length === 0 && <p style={{textAlign:'center', color:'#64748B', padding:'2rem'}}>No new leads at the moment.</p>}
                            {jobLeads.slice(0, 5).map(job => (
                                <div key={job.job_id} className={styles.listItem}>
                                    <div className={styles.itemMain}>
                                        <h4>{job.name} {isUrgent(job.deadline) && <span className={styles.urgentBadge}>URGENT</span>}</h4>
                                        {job.assignedBusinessId == loggedInBusinessId && (<div className={styles.assignedBadge}><User size={12}/> Assigned to you</div>)}
                                        <div className={styles.itemMeta}>
                                            <span><MapPin size={14}/> {job.postcode}</span>
                                            <span><Clock size={14}/> {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}</span>
                                        </div>
                                    </div>
                                    <div style={{textAlign:'right'}}>
                                        <div className={styles.priceTag}>{job.budgetAmount ? `$${job.budgetAmount}` : (job.budget || "N/A")}</div>
                                        <button className={styles.btnView} style={{marginTop:'0.5rem'}} onClick={() => navigate("/business-jobs-dashboard", { state: { selectedJob: job } })}>View Details</button>
                                    </div>
                                </div>
                            ))}
                            {jobLeads.length > 0 && <NavLink to="/business-jobs-dashboard" style={{display:'block', textAlign:'center', color:'#D97706', fontWeight:'600', marginTop:'1.5rem', textDecoration:'none'}}>View All Leads →</NavLink>}
                        </>
                    )}

                    {activeTab === 'work' && (
                        <>
                            {isLoadingWork && <p>Loading projects...</p>}
                            {!isLoadingWork && myProjects.length === 0 && myPostedJobs.length === 0 && (
                                <div style={{textAlign:'center', padding:'3rem', color:'#64748B'}}>
                                    <p>You haven't created any projects or jobs yet.</p>
                                    <button onClick={handleCreateProject} style={{marginTop:'1rem', color:'#D97706', background:'none', border:'none', cursor:'pointer', fontWeight:600}}>+ Create Your First Project</button>
                                </div>
                            )}
                            
                            {myProjects.length > 0 && (
                                <div style={{marginBottom: '2rem'}}>
                                    <h3 style={{fontSize: '1rem', fontWeight: '600', color: '#334155', marginBottom: '1rem', display: 'flex', alignItems: 'center'}}>
                                        <Folder size={16} style={{marginRight: '8px', color: '#2563EB'}}/> Projects
                                    </h3>
                                    {myProjects.slice(0, 8).map(project => (
                                        <div key={project.projectId || project.ProjectId} className={styles.listItem}>
                                            <div className={styles.itemMain}>
                                                <h4>{project.name || project.Name}</h4>
                                                <div className={styles.itemMeta}><span style={{color:'#D97706', fontWeight:600}}>Status: {project.status || project.Status || 'Planning'}</span></div>
                                            </div>
                                            <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
                                                <button className={styles.btnView} onClick={() => navigate(`/project/${project.projectId || project.ProjectId}`)}>Manage</button>
                                            </div>
                                        </div>
                                    ))}
                                    <button 
                                        className={styles.linkButton} 
                                        style={{display:'block', width:'auto', margin:'0.5rem auto'}} 
                                        onClick={() => navigate('/projects')}
                                    >
                                        View All Projects →
                                    </button>
                                </div>
                            )}

                            {myPostedJobs.length > 0 && (
                                <div>
                                    <h3 style={{fontSize: '1rem', fontWeight: '600', color: '#334155', marginBottom: '1rem', display: 'flex', alignItems: 'center'}}>
                                        <Wrench size={16} style={{marginRight: '8px', color: '#D97706'}}/> One-off Jobs
                                    </h3>
                                    {myPostedJobs.slice(0, 8).map(job => (
                                        <div key={job.job_id || job.JobId || job.id || job.Id} className={styles.listItem}>
                                            <div className={styles.itemMain}>
                                                <h4>{job.name || job.Name || 'Untitled Job'}</h4>
                                                <div className={styles.itemMeta}>
                                                    <span style={{color:'#16A34A', fontWeight:600}}>{job.jobStatus || job.JobStatus || job.status || job.Status}</span>
                                                    <span><Clock size={12}/>{job.created_at || job.CreatedAt ? new Date(job.created_at || job.CreatedAt).toLocaleDateString() : "N/A"}</span>
                                                </div>
                                            </div>
                                            <div style={{textAlign:'right'}}>
                                                <button className={styles.btnView} onClick={() => navigate(`/job-console/${job.job_id || job.JobId || job.id || job.Id}`)}>Manage</button>
                                            </div>
                                        </div>
                                    ))}
                                    <button 
                                        className={styles.linkButton} 
                                        style={{display:'block', width:'auto', margin:'0.5rem auto'}} 
                                        onClick={() => navigate('/my-jobs')}
                                    >
                                        View All One Off Jobs →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
          </div>

          {/* RIGHT COLUMN: SIDEBAR */}
          <div className={styles.sidebar}>
            
            {/* 1. NEW STATUS WIDGET (Top Priority) */}
            {renderBadgeStatus()}

            {/* 2. QUICK ACTIONS */}
            <div className={styles.sidebarCard}>
                <div className={styles.sidebarHeader}><Plus size={18} color="#0F172A"/> Quick Actions</div>
                <FeatureGuard isSubscribed={businessProfile?.businesses.isVerified} featureName="AI Project Assistant" benefitText="Let our AI Project and Job Assistant help you find sub-constractors your jobs.">
                    <button className={`${styles.actionBtn} ${styles.btnPrimary}`} onClick={handleCreateProject}><Folder size={18}/> Create New Project</button>
                </FeatureGuard>
                <FeatureGuard isSubscribed={businessProfile?.businesses.isVerified} featureName="AI Job Assistant" benefitText="Let our AI Kob Assistant help you find sub-constractors your jobs.">
                    <button className={`${styles.actionBtn} ${styles.btnSecondary}`} onClick={handlePostSubContractJob}><Users size={18}/> Post SubContract Job</button>
                </FeatureGuard>
            </div>

            {/* 3. PROFILE STRENGTH */}
            <div className={styles.sidebarCard}>
                <div className={styles.sidebarHeader}><Settings size={18} color="#0F172A"/> Profile Strength</div>
                <ul className={styles.checklist}>
                    <li className={styles.checklistItem}>
                        <div className={`${styles.dot} ${businessProfile?.workPortfolio?.model?.length > 0 ? styles.done : styles.todo}`}></div> Portfolio Added
                    </li>
                    <li className={styles.checklistItem}>
                        <div className={`${styles.dot} ${businessProfile?.customerReviews?.length > 0 ? styles.done : styles.todo}`}></div> Reviews Collected
                    </li>
                    <li className={styles.checklistItem}>
                        <div className={`${styles.dot} ${businessProfile?.statistics?.googleRating ? styles.done : styles.todo}`}></div> Google Rating Imported
                    </li>
                </ul>
                <button className={styles.linkButton} style={{marginTop:'1rem'}} onClick={handleEditProfile}>Improve Profile →</button>
            </div>

            {/* 4. SAVED BUSINESSES */}
            <div className={styles.sidebarCard}>
                <div className={styles.sidebarHeader}><Heart size={18} color="#0F172A"/> Saved Businesses</div>
                {isLoadingSaved ? <p>Loading...</p> : savedBusinessesData.slice(0,3).map(biz => (
                    <div key={biz.businessId} style={{display:'flex', gap:'10px', marginBottom:'10px', alignItems:'center'}}>
                        <Avatar style={{width:32, height:32}}>
                            <AvatarImage src={biz.profilePic || "/placeholder.svg"}/>
                            <AvatarFallback>{biz.businessName?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div style={{fontSize:'0.9rem', fontWeight:600}}>{biz.businessName}</div>
                    </div>
                ))}
                {savedBusinessesData.length === 0 && <p style={{fontSize:'0.8rem', color:'#64748B'}}>No saved businesses yet.</p>}
                <button className={styles.linkButton} onClick={() => navigate('/saved-businesses')}>View All →</button>
            </div>

          </div>
        </div>
      </div>

      {/* --- MODALS --- */}
      <ImportGoogleRatingModal isOpen={isGoogleModalOpen} onClose={() => setIsGoogleModalOpen(false)} onImportSuccess={handleImportSuccess} businessId={loggedInBusinessId} />
      <CreateProjectModal isOpen={isCreateProjectModalOpen} onClose={() => setIsCreateProjectModalOpen(false)} />

      <FindContractorsModal 
          isOpen={isFindContractorsModalOpen} 
          onClose={() => setIsFindContractorsModalOpen(false)} 
          onPostJob={handlePostJob} 
          onBrowsePros={handleBrowsePros} 
          taskName={"One-off Job"} 
      />

    </div>
  );
};

export default BusinessDashboardPureCSS;
